// src/pages/admin/EventEditPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { db } from '../../firebase';
import EventEditBinding from '../../components/events/edit/EventEditBinding';

export default function EventEditPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [eventData, setEventData] = useState(null);
  const [heroUrl, setHeroUrl]   = useState(''); // HTTP URL 또는 빈 문자열
  const [loading, setLoading]   = useState(true);

  // ==============================
  // convertToWebp: PNG/JPEG 등 이미지를 WebP Blob으로 변환
  // ==============================
  const convertToWebp = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('WebP 변환 실패'));
              }
            },
            'image/webp',
            1.0 // 품질: 0.0 ~ 1.0 (0.8 권장)
          );
        };
        img.onerror = () => reject(new Error('이미지 로딩 실패'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('FileReader 실패'));
      reader.readAsDataURL(file);
    });
  };

  // ==============================
  // uploadFileWithCache: Blob/File 객체 업로드 + Cache-Control 설정
  // ==============================
  const uploadFileWithCache = async (blobOrFile, folderPath, filename) => {
    const storage = getStorage();
    const fileRef = storageRef(storage, `${folderPath}/${filename}`);
    const metadata = {
      contentType: blobOrFile.type,
      cacheControl: 'public, max-age=43200', // 12시간 캐시
    };
    await uploadBytes(fileRef, blobOrFile, metadata);
    const url = await getDownloadURL(fileRef);
    return url;
  };

  useEffect(() => {
    if (!eventId) return;

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'events', eventId));
        if (!snap.exists()) {
          alert('해당 이벤트를 찾을 수 없습니다.');
          navigate('/admin', { state: { tab: 'events' } });
          return;
        }

        const data = snap.data();
        // 편집에 필요한 기존 데이터를 state에 세팅
        setEventData({
          title:     data.title     || '',
          subtitle:  data.subtitle  || '',
          startDate: data.startDate || '',
          endDate:   data.endDate   || '',
          content:   data.content   || '',
        });

        // Firestore 문서에 저장된 heroImagePath 필드를 읽어서,
        // ① “HTTP URL”인 경우: 그대로 heroUrl에 세팅
        // ② “스토리지 경로”인 경우: getDownloadURL() 호출 후 heroUrl에 세팅
        const storedPath = data.heroImagePath || '';
        if (storedPath.startsWith('http://') || storedPath.startsWith('https://')) {
          setHeroUrl(storedPath);
        } else if (storedPath) {
          try {
            const storage = getStorage();
            const url = await getDownloadURL(storageRef(storage, storedPath));
            setHeroUrl(url);
          } catch (err) {
            console.warn(`Storage에서 hero 다운로드 URL 가져오기 실패 (${storedPath}):`, err);
            setHeroUrl('');
          }
        }
      } catch (err) {
        console.error(err);
        alert('데이터 불러오기 중 오류가 발생했습니다.');
        navigate('/admin', { state: { tab: 'events' } });
        return;
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, navigate]);

  const handleSave = async ({ title, subtitle, startDate, endDate, content, heroFile }) => {
    try {
      let newHeroUrl = heroUrl;

      // 새 파일이 선택된 경우 업로드하여 HTTP URL 획득
      if (heroFile) {
        if (heroFile.type.startsWith('image/')) {
          // WebP로 변환
          const webpBlob = await convertToWebp(heroFile);
          const webpFilename = `${eventId}_hero`;
          newHeroUrl = await uploadFileWithCache(
            webpBlob,
            `events/${eventId}`,
            webpFilename
          );
        } else {
          // 이미지가 아닐 경우 원본 그대로 업로드
          const filename = `${eventId}_hero`;
          newHeroUrl = await uploadFileWithCache(
            heroFile,
            `events/${eventId}`,
            filename
          );
        }
      }

      await updateDoc(doc(db, 'events', eventId), {
        title,
        subtitle,
        startDate,
        endDate,
        content,
        // newHeroUrl이 빈 문자열이 아니라면 heroImagePath 필드로 업데이트
        ...(newHeroUrl ? { heroImagePath: newHeroUrl } : {})
      });

      alert('이벤트 수정 완료!');
      navigate('/admin', { state: { tab: 'events' } });
    } catch (err) {
      console.error(err);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return <div className="py-20 text-center text-gray-400">로딩 중…</div>;
  }

  return (
    <EventEditBinding
      event={{
        ...eventData,
        // EventEditBinding 쪽에서 heroImagePath prop으로 화면에 미리보기 또는 배경을 표시하게 됨
        heroImagePath: heroUrl,
      }}
      onSubmit={handleSave}
    />
  );
}
